import { type ClockSchedule, clockOffsetAt, clockSchedule, formatUtcOffset } from '@felix/domain';
import { IconClockEdit } from '@tabler/icons-react';
import { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { shipClockOf } from '../personnel/ship-clock.js';
import type { PersonnelVessel } from '../personnel/types.js';
import type { VoyageSource } from './source.js';

/**
 * Sefer raporlarında gemi saati (ADR 0065 §8): raporun LT farkının varsayılanı gemi saati
 * kaydından; rapor kendi farkını tutar, kayıttan farklıysa uyarılır, kendiliğinden üzerine
 * yazılmaz.
 */
export const VoyageClockContext = createContext<ClockSchedule | null>(null);

/** Geminin saat çizelgesi: dinlenme raporunun taşıdığı saat kaydından; okunamazsa dilim. */
export async function loadVoyageClock(
  source: Pick<VoyageSource, 'restHours'>,
  vessel: Pick<PersonnelVessel, 'id' | 'timeZone'>,
): Promise<ClockSchedule> {
  try {
    const day = new Date().toISOString().slice(0, 10);
    const result = await source.restHours(vessel.id, day, day);
    return shipClockOf(vessel.timeZone, result.clockChanges ?? []);
  } catch {
    return clockSchedule(vessel.timeZone);
  }
}

/** Yeni raporun varsayılan farkı: saat kaydının o andaki farkı, yuvarlanmadan (ADR 0065 §8). */
export function defaultReportOffset(clock: ClockSchedule, at: number): number {
  return clockOffsetAt(clock, at);
}

/** Raporun farkı saat kaydından farklıysa uyarı (tutarsızlık; otomatik düzeltme yok). */
export function ClockLogHint({
  reportedAt,
  offset,
  className,
}: {
  reportedAt: string | null | undefined;
  offset: number;
  className?: string | undefined;
}) {
  const { t } = useTranslation();
  const clock = useContext(VoyageClockContext);
  if (!clock || !reportedAt || Number.isNaN(Date.parse(reportedAt))) return null;
  const expected = clockOffsetAt(clock, Date.parse(reportedAt));
  if (expected === offset) return null;
  return (
    <p className={className} role="status" data-clock-mismatch>
      <IconClockEdit size={16} aria-hidden />{' '}
      {t('voyage.clockHint', {
        log: formatUtcOffset(expected),
        report: formatUtcOffset(offset),
      })}
    </p>
  );
}
